import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Inbox as InboxIcon, LifeBuoy, ShieldAlert, Bell, Search, Sparkles, Send, ArrowRight, User as UserIcon } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ViewTabs, ModerationMenu } from './ModerationControls';
import { supportStats, supportList, supportGet, supportUpdate, supportDraft, supportReply, supportHide, supportRestore, supportDelete, notifRecipients, sendNotification, listNotifications } from '../lib/marketingApi';

const CATS = [
  { key: 'enquiries', label: 'Enquiries', icon: InboxIcon },
  { key: 'support', label: 'Support', icon: LifeBuoy },
  { key: 'dispute', label: 'Disputes', icon: ShieldAlert },
  { key: 'notifications', label: 'Notifications', icon: Bell },
];
const STATUS_STYLE = {
  new: 'bg-sky-100 text-sky-700', open: 'bg-amber-100 text-amber-700',
  responded: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-100 text-slate-500',
};

function fmt(iso) { if (!iso) return ''; try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; } }

export default function Inbox() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useAdminAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState(sp.get('tab') && ['enquiries', 'support', 'dispute', 'notifications'].includes(sp.get('tab')) ? sp.get('tab') : 'support');
  const [view, setView] = useState('active');
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState(null);

  // composer
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [instructions, setInstructions] = useState('');
  const [model, setModel] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  // notifications
  const [nSubject, setNSubject] = useState('');
  const [nBody, setNBody] = useState('');
  const [nCount, setNCount] = useState(null);
  const [nHistory, setNHistory] = useState([]);
  const [nSending, setNSending] = useState(false);

  const loadNotif = useCallback(() => {
    notifRecipients().then((d) => setNCount(d.count)).catch(() => {});
    listNotifications().then((d) => setNHistory(d.items || [])).catch(() => {});
  }, []);
  useEffect(() => { if (tab === 'notifications') loadNotif(); }, [tab, loadNotif]);

  const sendBlast = async () => {
    if (!nSubject.trim() || !nBody.trim()) { toast.error('Add a subject and message.'); return; }
    if (!window.confirm(`Send this notification to ${nCount ?? 'all'} platform users?`)) return;
    setNSending(true);
    try {
      const d = await sendNotification({ subject: nSubject, body: nBody, scope: 'all' });
      toast.success(`Sending to ${d.recipient_count} platform users`);
      setNSubject(''); setNBody('');
      setTimeout(loadNotif, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); } finally { setNSending(false); }
  };

  const loadStats = useCallback(() => { supportStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const isTicketTab = tab === 'support' || tab === 'dispute';

  const load = useCallback(async () => {
    if (!isTicketTab) { setItems([]); return; }
    setItems(null); setSel(null);
    try {
      const d = await supportList({ category: tab, view, search: search || undefined, limit: 200 });
      setItems(d.items || []);
    } catch (e) { toast.error('Failed to load'); setItems([]); }
  }, [tab, view, search, isTicketTab]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const open = async (id) => {
    try {
      const d = await supportGet(id);
      setSel(d.ticket);
      setSubject(`Re: ${d.ticket.subject || 'your message'}`);
      setBodyText(''); setInstructions('');
    } catch (e) { toast.error('Failed to open'); }
  };

  useEffect(() => {
    const openId = sp.get('open');
    if (openId && isTicketTab && items) { open(openId); setSp({ tab }, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const draft = async () => {
    if (!sel) return;
    setDrafting(true);
    try {
      const d = await supportDraft(sel.id, { instructions, model: model || undefined });
      setSubject(d.draft.subject);
      setBodyText(d.draft.body);
      toast.success(`Draft ready (${d.draft.model === 'claude' ? 'Claude' : 'GPT'})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Draft failed'); } finally { setDrafting(false); }
  };

  const send = async () => {
    if (!sel || !subject.trim() || !bodyText.trim()) { toast.error('Add a subject and message.'); return; }
    setSending(true);
    try {
      const d = await supportReply(sel.id, { subject, body: bodyText });
      setSel(d.ticket); setBodyText('');
      toast.success('Reply sent via Outlook');
      loadStats(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed — check Outlook setup'); } finally { setSending(false); }
  };

  const setStatus = async (s) => { if (!sel) return; try { const d = await supportUpdate(sel.id, { status: s }); setSel(d.ticket); loadStats(); load(); } catch (e) { toast.error('Update failed'); } };
  const setCat = async (c) => { if (!sel) return; try { const d = await supportUpdate(sel.id, { category: c }); setSel(d.ticket); loadStats(); load(); toast.success(`Tagged as ${c}`); } catch (e) { toast.error('Update failed'); } };
  const reload = () => { load(); loadStats(); };
  const doHide = async (id, reason) => { try { await supportHide(id, reason); toast.success(reason === 'spam' ? 'Marked as spam' : 'Moved to trash'); if (sel?.id === id) setSel(null); reload(); } catch (e) { toast.error('Action failed'); } };
  const doRestore = async (id) => { try { await supportRestore(id); toast.success('Restored'); reload(); } catch (e) { toast.error('Restore failed'); } };
  const doPurge = async (id) => { if (!window.confirm('Permanently delete this ticket?')) return; try { await supportDelete(id); toast.success('Deleted'); if (sel?.id === id) setSel(null); reload(); } catch (e) { toast.error('Delete failed'); } };

  const count = (k) => (stats?.by_category?.[k] ?? 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inbox-page">
      <AdminHeader active="inbox" />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><InboxIcon className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Inbox</h1>
            <p className="text-sm text-slate-500">Enquiries, support, disputes & platform notifications — reply from support@billportal.io</p>
          </div>
        </div>

        {/* Category tabs */}
        <div className="mb-4 inline-flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {CATS.map((c) => (
            <button key={c.key} onClick={() => setTab(c.key)} data-testid={`inbox-tab-${c.key}`}
              className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-all ${tab === c.key ? 'bg-[#0035A8] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              <c.icon className="h-4 w-4" /> {c.label}
              {(c.key === 'support' || c.key === 'dispute') && <span className={`ml-1 rounded-full px-1.5 text-xs ${tab === c.key ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{count(c.key)}</span>}
            </button>
          ))}
        </div>

        {tab === 'enquiries' && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="inbox-enquiries">
            <InboxIcon className="mx-auto h-10 w-10 text-[#0035A8]" />
            <h3 className="mt-3 text-lg font-bold text-slate-900">Website enquiries live in the Enquiries CRM</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Leads from the contact & demo forms, with owner assignment, notes and booking links.</p>
            <button onClick={() => navigate('/admin/enquiries')} data-testid="inbox-open-enquiries" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#0035A8] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#002a86]">Open Enquiries <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}

        {tab === 'notifications' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12" data-testid="inbox-notifications">
            <div className="lg:col-span-6">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Bell className="h-4 w-4 text-[#0035A8]" /> New platform notification</div>
                <p className="mt-1 text-xs text-slate-500">Sends to <strong>{nCount === null ? '…' : nCount}</strong> <strong>active</strong> platform users as <strong>notifications@billportal.io</strong>. Inactive/churned orgs & disabled users are excluded. Use for Terms updates, releases & service notices — not marketing.</p>
                <input value={nSubject} onChange={(e) => setNSubject(e.target.value)} placeholder="Subject" data-testid="notif-subject"
                  className="mt-4 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-[#0035A8]" />
                <textarea value={nBody} onChange={(e) => setNBody(e.target.value)} rows={9} placeholder="Write the announcement…" data-testid="notif-body"
                  className="mt-2 w-full rounded-md border border-slate-300 bg-white p-3 text-sm leading-relaxed outline-none focus:border-[#0035A8]" />
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-400">Every send is logged with sender & time</span>
                  <button onClick={sendBlast} disabled={nSending} data-testid="notif-send-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0035A8] px-5 py-2 text-sm font-semibold text-white hover:bg-[#002a86] disabled:opacity-50">
                    {nSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to all
                  </button>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-xl border border-slate-200 bg-white">
                <div className="border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-800">Sent history</div>
                {nHistory.length === 0 ? (
                  <div className="flex flex-col items-center gap-1.5 py-12 text-slate-400"><Bell className="h-6 w-6" /><p className="text-xs">No notifications sent yet</p></div>
                ) : (
                  <div className="max-h-[520px] overflow-y-auto">
                    {nHistory.map((n) => (
                      <div key={n.id} className="border-b border-slate-50 px-5 py-3" data-testid={`notif-row-${n.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-slate-900">{n.subject}</span>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${n.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : n.status === 'queued' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{n.status}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {n.sent_count || 0}/{n.recipient_count || 0} delivered{n.failed_count ? ` · ${n.failed_count} failed` : ''}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400">by {n.sent_by} · {fmt(n.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isTicketTab && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {/* List */}
            <div className="lg:col-span-5">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <ViewTabs view={view} setView={setView} counts={{ spam: stats?.hidden_spam, deleted: stats?.hidden_deleted }} testidPrefix="inbox" />
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" data-testid="inbox-search"
                    className="h-9 w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 text-sm outline-none focus:border-[#0035A8]" />
                </div>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {items === null ? (
                  <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : items.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-14 text-slate-400"><InboxIcon className="h-7 w-7" /><p className="text-sm">Nothing here yet.</p></div>
                ) : items.map((t) => (
                  <div key={t.id} role="button" tabIndex={0} onClick={() => open(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') open(t.id); }} data-testid={`inbox-row-${t.id}`}
                    className={`flex cursor-pointer items-start gap-2 border-b border-slate-100 px-4 py-3 transition-colors hover:bg-slate-50 ${sel?.id === t.id ? 'bg-[#0035A8]/5' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900">{t.name}</span>
                        {t.status === 'new' && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />}
                      </div>
                      <div className="truncate text-xs text-slate-500">{t.subject}</div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize ${STATUS_STYLE[t.status] || 'bg-slate-100 text-slate-500'}`}>{t.status}</span>
                        <span className="text-[10px] text-slate-400">{fmt(t.created_at)}</span>
                      </div>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <ModerationMenu view={view} isAdmin={isAdmin} testid={`ticket-${t.id}`} onSpam={() => doHide(t.id, 'spam')} onDelete={() => doHide(t.id, 'deleted')} onRestore={() => doRestore(t.id)} onPurge={() => doPurge(t.id)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detail + composer */}
            <div className="lg:col-span-7">
              {!sel ? (
                <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400" data-testid="inbox-empty-detail">
                  <InboxIcon className="h-8 w-8" /><p className="mt-2 text-sm">Select a message to read and reply</p>
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-white" data-testid="inbox-detail">
                  <div className="border-b border-slate-100 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate text-lg font-bold text-slate-900">{sel.subject}</h2>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          <span className="inline-flex items-center gap-1"><UserIcon className="h-3.5 w-3.5" />{sel.name}</span>
                          <a href={`mailto:${sel.email}`} className="text-[#0035A8] hover:underline">{sel.email}</a>
                          {sel.company && <span>· {sel.company}</span>}
                          {sel.org_id && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600" data-testid="inbox-org-id">Org: {sel.org_id}</span>}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <select value={sel.category} onChange={(e) => setCat(e.target.value)} data-testid="inbox-category-select" title="Tag this request (e.g. mark as a dispute)"
                          className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 outline-none">
                          {['support', 'billing', 'general', 'dispute'].map((s) => <option key={s} value={s}>{s === 'dispute' ? '⚑ dispute' : s}</option>)}
                        </select>
                        <select value={sel.status} onChange={(e) => setStatus(e.target.value)} data-testid="inbox-status-select"
                          className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 outline-none">
                          {['new', 'open', 'responded', 'closed'].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <p className="mt-4 whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">{sel.message}</p>
                  </div>

                  {/* Prior replies */}
                  {(sel.replies || []).length > 0 && (
                    <div className="border-b border-slate-100 p-5">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Activity</div>
                      <div className="space-y-3">
                        {sel.replies.map((r) => (
                          <div key={r.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span className={`rounded px-1.5 py-0.5 font-medium ${r.channel === 'email' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{r.channel === 'email' ? 'Email sent' : 'Note'}</span>
                              <span>{r.by}</span><span>· {fmt(r.at)}</span>
                            </div>
                            {r.subject && <div className="mt-1 text-sm font-semibold text-slate-800">{r.subject}</div>}
                            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{r.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Composer */}
                  <div className="p-5">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Reply</span>
                      <div className="ml-auto flex items-center gap-2">
                        <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="inbox-model-select" className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-600 outline-none">
                          <option value="">Auto ({tab === 'dispute' ? 'Claude' : 'GPT'})</option>
                          <option value="gpt">GPT</option>
                          <option value="claude">Claude</option>
                        </select>
                        <button onClick={draft} disabled={drafting} data-testid="inbox-draft-btn"
                          className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-60">
                          {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI draft
                        </button>
                      </div>
                    </div>
                    <input value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Optional: tell the AI what to say (e.g. offer a goodwill credit)" data-testid="inbox-instructions"
                      className="mb-2 h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-xs outline-none focus:border-[#0035A8]" />
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="inbox-reply-subject"
                      className="mb-2 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-[#0035A8]" />
                    <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} placeholder="Write your reply, or generate one with AI and edit it here…" data-testid="inbox-reply-body"
                      className="w-full rounded-md border border-slate-300 bg-white p-3 text-sm leading-relaxed outline-none focus:border-[#0035A8]" />
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-400">Sends from <strong>support@billportal.io</strong> via email</span>
                      <button onClick={send} disabled={sending} data-testid="inbox-send-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0035A8] px-5 py-2 text-sm font-semibold text-white hover:bg-[#002a86] disabled:opacity-50">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
